import React, { useState, useEffect } from 'react'
import { withStyles, WithStyles, createStyles } from '@material-ui/core'
import ReactTooltip from 'react-tooltip'
import { useSelector } from 'react-redux'
import Input from 'uiKit/inputs/Input'
import Select from 'uiKit/Select/Select'
import DatePicker from 'react-datepicker'
import styled from 'styled-components'
import 'react-datepicker/dist/react-datepicker.css'
import { getUsersList, getSupervisionTemplate, duplicateSupervisionTemplate } from '../../api/supervision'
import { components } from 'react-select'
import { EditIcon } from 'uiKit/icons/EditIcon'
import LoaderScreen from 'uiKit/loaders/loaderScreen'
import { MINE_SHAFT } from 'constants/colors'

// Custom Menu component with Select All option
const Menu = props => {
  const { selectProps } = props
  const { onSelectAll } = selectProps

  return (
    <components.Menu {...props}>
      {props.options.length > 0 && (
        <div
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid var(--color-ghost-grey)',
            cursor: 'pointer',
            fontWeight: 500,
            color: 'var(--color-text-secondary)',
          }}
          onClick={onSelectAll}>
          Select All
        </div>
      )}
      {props.children}
    </components.Menu>
  )
}

const FormGroup = styled.div`
  margin-bottom: 16px;
`

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-form-label);
  margin-bottom: 8px;

  span {
    color: var(--color-text-error);
  }
  & .modal-tooltip {
    width: 250px;
    padding: 8px;
    border: 1px solid var(--color-issabeline-grey);
    box-sizing: border-box;
    box-shadow: none;
    border-radius: 10px;
    z-index: 9999;
    opacity: 1;
    &.type-dark {
      background-color: ${MINE_SHAFT};
    }
  }
`

const FlexContainer = styled.div`
  display: flex;
  gap: 20px;
`

const DatePickerWrapper = styled.div`
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container {
    position: relative;
    display: block;
  }
  .react-datepicker__input-container input {
    width: 100%;
    height: 40px;
    border-radius: 4px;
    border: 1px solid var(--color-ghost-grey);
    padding: 0 40px 0 12px; /* Increased right padding to make room for the icon */
    font-size: 14px;
    color: var(--color-text-primary);
    &:focus {
      outline: none;
      border-color: var(--color-primary);
    }
    &::placeholder {
      color: var(--color-text-placeholder);
    }
  }
  .react-datepicker__calendar-icon {
    position: absolute;
    right: 8px;
    top: -1px;
    cursor: pointer;
  }
`

const styles = createStyles({
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '26px 0 26px 0',
    overflowY: 'auto',
  },
  formContent: {
    padding: '0 24px',
    flex: 1,
    overflowY: 'auto',
  },
  confirmModalHeader: {
    fontSize: '20px',
    fontWeight: 600,
    color: 'var(--color-oxford-blue)',
    lineHeight: '18px',
    padding: '0 24px',
    marginBottom: '16px',
  },
  confirmModalContent: {
    fontSize: '20px',
    color: 'var(--color-text-secondary)',
    marginTop: '24px',
    padding: '0 24px',
    lineHeight: '24px',
    marginBottom: '0px',
  },
  customModalPaperDuplicate: {
    padding: '0px!important',
    height: '600px!important',
    width: '701px!important',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
    padding: '0 24px',
    gap: '14px',
    borderTop: '1px solid #E0E0E0',
    paddingTop: '25px',
  },
  createButton: {
    padding: '8px 12px',
    border: 0,
    background: 'var(--color-button-primary)',
    borderRadius: 10,
    cursor: 'pointer',
    color: 'var(--color-white)',
    fontSize: '16px',
    fontWeight: 500,
    width: '147px',
  },
  cancelButton: {
    padding: '8px 12px',
    border: '1px solid var(--color-button-primary)',
    background: 'var(--color-white)',
    borderRadius: 10,
    cursor: 'pointer',
    color: 'var(--color-button-primary)',
    fontSize: '16px',
    fontWeight: 500,
    width: '147px',
  },
})

interface DuplicateSupervisionProps extends WithStyles<typeof styles> {
  onConfirm: () => void
  onCancel: () => void
  templateId: number
}

const frequencyOptions = [
  { label: 'One time', value: 'ONE_TIME' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Quarterly', value: 'QUARTERLY' },
  { label: '6 months', value: 'HALF_YEARLY' },
  { label: 'Annually', value: 'ANNUAL' },
  { label: 'Alternate months', value: 'ALTERNATE_MONTHS' },
]

interface CustomInputProps {
  value?: string
  onClick?: () => void
  placeholder?: string
  className?: string
}
const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  ({ value, onClick, placeholder, className }, ref) => (
    <div className="custom-input-container" style={{ position: 'relative' }}>
      <input value={value || ''} onClick={onClick} placeholder={placeholder} className={className} readOnly ref={ref} />
      <div className="react-datepicker__calendar-icon" onClick={onClick} style={{ cursor: 'pointer' }}>
        <EditIcon />
      </div>
    </div>
  ),
)

// Helper function to format date with UTC noon time
const formatDateWithUTCNoon = (date: Date): string => {
  if (!date) return null
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0))
  return utcDate.toISOString()
}

const DuplicateSupervision: React.FC<DuplicateSupervisionProps> = ({ classes, onConfirm, onCancel, templateId }) => {
  const activeBot = useSelector((state: any) => state.activeBot)
  const [userOptions, setUserOptions] = useState([])
  const [isUsersLoading, setIsUsersLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({
    name: '',
    frequency: null,
    startDate: null,
    dueDate: null,
    carers: [],
  })

  const [isFrequencyMenuOpen, setIsFrequencyMenuOpen] = useState(false)
  const [isUsersMenuOpen, setIsUsersMenuOpen] = useState(false)

  const getFrequencyLabel = (frequency: string): string => {
    const frequencyMap = {
      ONE_TIME: 'One time',
      MONTHLY: 'Monthly',
      QUARTERLY: 'Quarterly',
      HALF_YEARLY: '6 months',
      ANNUAL: 'Annually',
      ALTERNATE_MONTHS: 'Alternate months',
    }
    return frequencyMap[frequency] || frequency
  }

  const prefillFormWithTemplateData = templateData => {
    if (!templateData) return

    const frequencyOption = {
      value: templateData.frequency,
      label: getFrequencyLabel(templateData.frequency),
    }

    const selectedUsers =
      templateData.users?.map(user => ({
        value: user.id,
        label: user.fullName,
      })) || []

    setForm({
      name: `Copy of ${templateData.name}`,
      frequency: frequencyOption,
      carers: [],
      startDate: '',
      dueDate: '',
    })
  }
  useEffect(() => {
    if (activeBot?.id && templateId) {
      setLoading(true)
      getSupervisionTemplate(activeBot.id, templateId)
        .then(response => {
          prefillFormWithTemplateData(response?.data)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [activeBot, templateId])

  const [errors, setErrors] = useState({
    name: '',
    frequency: '',
    startDate: '',
    dueDate: '',
    carers: '',
  })

  const onChangeHandler = (field, value) => {
    setForm(prevState => ({
      ...prevState,
      [field]: value,
    }))

    setErrors(prevState => ({
      ...prevState,
      [field]: '',
    }))
  }

  // Handle select all users (inside component scope)
  const handleSelectAll = () => {
    if (userOptions && userOptions.length > 0) {
      onChangeHandler('carers', userOptions)
    }
  }

  const validationRules = {
    name: value => (!value ? 'Name is required' : ''),
    frequency: value => (!value ? 'Frequency is required' : ''),
    startDate: (value, form) => (form.frequency?.value === 'ONE_TIME' && !value ? 'Start date is required' : ''),
    dueDate: (value, form) => (form.frequency?.value === 'ONE_TIME' && !value ? 'Due date is required' : ''),
    carers: value => (!value || value.length === 0 ? 'At least one user is required' : ''),
  }

  const validateField = (field, value) => {
    return validationRules[field] ? validationRules[field](value, form) : ''
  }
  const validateForm = () => {
    const newErrors = {
      name: validateField('name', form.name),
      frequency: validateField('frequency', form.frequency),
      startDate: validateField('startDate', form.startDate),
      dueDate: validateField('dueDate', form.dueDate),
      carers: validateField('carers', form.carers),
    }

    setErrors(newErrors)
    return !Object.values(newErrors).some(error => error !== '')
  }
  // Check if all required fields are filled
  const isFormValid = () => {
    // Check if name is filled
    if (!form.name.trim()) return false

    // Check if frequency is selected
    if (!form.frequency) return false

    // For ONE_TIME frequency, check if dates are selected
    if (form.frequency.value === 'ONE_TIME') {
      if (!form.startDate || !form.dueDate) return false
    }

    // Check if at least one user is assigned
    if (!form.carers || form.carers.length === 0) return false

    return true
  }

  const handleSubmit = () => {
    if (!validateForm()) {
      return
    }
    setLoading(true)
    const payload = {
      name: form.name,
      frequency: form.frequency?.value,
      startDate: form.startDate,
      dueDate: form.dueDate,
      isDraft: true,
      userIds: form.carers.map(user => user.value),
    }
    duplicateSupervisionTemplate(activeBot.id, templateId, payload)
      .then(response => {
        onConfirm()
      })
      .finally(() => {
        setLoading(false)
      })
  }

  if (loading) {
    return (
      <div className={classes.modalContainer}>
        <h2 className={classes.confirmModalHeader}>Duplicate</h2>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <LoaderScreen
            size={{
              circleSize: 60,
              border: 8,
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={classes.modalContainer}>
      <h2 className={classes.confirmModalHeader}>Duplicate</h2>
      <div className={classes.formContent}>
        <FormGroup>
          <FormLabel>
            Name <span>*</span>
          </FormLabel>
          <Input
            name="name"
            placeholder="Enter name"
            value={form.name}
            onChange={e => onChangeHandler('name', e.target.value)}
            error={!!errors.name}
            errorMessage={errors.name}
            maxLength={64}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel>
            Frequency <span>*</span>
          </FormLabel>
          <Select
            name="frequency"
            placeholder="Select frequency"
            options={frequencyOptions}
            value={form.frequency}
            onChange={value => onChangeHandler('frequency', value)}
            error={!!errors.frequency}
            errorMessage={errors.frequency}
            closeMenuOnScroll={e => {
              const target = (e && (e.target as HTMLElement)) || null
              if (target && (target.closest('.cb-select__menu') || target.closest('.cb-select__menu-list'))) {
                // Scrolling inside the dropdown menu -> keep it open
                return false
              }
              if (isFrequencyMenuOpen) setIsFrequencyMenuOpen(false)
              return true
            }}
            menuIsOpen={isFrequencyMenuOpen}
            onMenuOpen={() => setIsFrequencyMenuOpen(true)}
            onMenuClose={() => setIsFrequencyMenuOpen(false)}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }), // Ensure dropdown is above modal
              menu: base => ({ ...base, zIndex: 9999 }),
            }}
          />
        </FormGroup>

        {form.frequency?.value === 'ONE_TIME' && (
          <FlexContainer>
            <FormGroup style={{ width: '50%' }}>
              <FormLabel>
                Available from <span>*</span>
                <ReactTooltip
                  className="modal-tooltip"
                  place="top"
                  type="dark"
                  effect="solid"
                  id="AvailableАromTooltip-1"
                  show={true}>
                  Care Professional will be able to access their supervision from this date.
                </ReactTooltip>
                <img
                  style={{ cursor: 'pointer', marginLeft: '10px' }}
                  data-tip
                  data-for="AvailableАromTooltip-1"
                  src="/images/platform/information-button.svg"
                  alt="info icon"
                />
              </FormLabel>
              <DatePickerWrapper>
                <DatePicker
                  selected={form.startDate ? new Date(form.startDate) : null}
                  onChange={date => onChangeHandler('startDate', date ? formatDateWithUTCNoon(date) : null)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select available from date"
                  className={errors.startDate ? 'error' : ''}
                  minDate={new Date()}
                  maxDate={form.dueDate ? new Date(form.dueDate) : undefined}
                  customInput={<CustomInput />}
                />
              </DatePickerWrapper>
              {errors.startDate && <div className="error-message">{errors.startDate}</div>}
            </FormGroup>
            <FormGroup style={{ width: '50%' }}>
              <FormLabel>
                Deadline <span>*</span>
              </FormLabel>
              <DatePickerWrapper>
                <DatePicker
                  selected={form.dueDate ? new Date(form.dueDate) : null}
                  onChange={date => onChangeHandler('dueDate', date ? formatDateWithUTCNoon(date) : null)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select deadline date"
                  className={errors.dueDate ? 'error' : ''}
                  minDate={form.startDate ? new Date(form.startDate) : new Date()}
                  customInput={<CustomInput />}
                />
              </DatePickerWrapper>
              {errors.dueDate && <div className="error-message">{errors.dueDate}</div>}
            </FormGroup>
          </FlexContainer>
        )}

        <FormGroup>
          <FormLabel>
            Assign users <span>*</span>
          </FormLabel>
          <Select
            name="carers"
            placeholder="Select users"
            options={userOptions}
            value={form.carers}
            onChange={value => onChangeHandler('carers', value)}
            error={!!errors.carers}
            errorMessage={errors.carers}
            isMulti
            menuPortalTarget={document.body}
            closeMenuOnScroll={e => {
              const target = (e && (e.target as HTMLElement)) || null
              if (target && (target.closest('.cb-select__menu') || target.closest('.cb-select__menu-list'))) {
                // Scrolling inside the dropdown menu -> keep it open
                return false
              }
              if (isUsersMenuOpen) setIsUsersMenuOpen(false)
              return true
            }}
            components={{ Menu }}
            onSelectAll={handleSelectAll}
            menuIsOpen={isUsersMenuOpen}
            onMenuClose={() => setIsUsersMenuOpen(false)}
            isLoading={isUsersLoading}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }), // Ensure dropdown is above modal
              menu: base => ({ ...base, zIndex: 9999 }),
            }}
            onMenuOpen={() => {
              setIsUsersMenuOpen(true)
              if (activeBot?.id) {
                setIsUsersLoading(true)
                getUsersList(activeBot.id)
                  .then(response => {
                    const combinedUsers = [...response.carers_list, ...response.managers_list]
                    setUserOptions(combinedUsers)
                  })
                  .catch(() => {
                    // Silently fail; optional: surface an error toast here
                  })
                  .finally(() => setIsUsersLoading(false))
              }
            }}
          />
        </FormGroup>
      </div>
      <div className={classes.buttonContainer}>
        <button className={classes.cancelButton} onClick={onCancel}>
          Cancel
        </button>
        <button
          className={classes.createButton}
          onClick={handleSubmit}
          disabled={!isFormValid()}
          style={{
            opacity: isFormValid() ? 1 : 0.6,
            cursor: isFormValid() ? 'pointer' : 'not-allowed',
          }}>
          Save
        </button>
      </div>
    </div>
  )
}

// PropTypes not needed with TypeScript interface

export default withStyles(styles, { withTheme: true })(DuplicateSupervision)
